import { isNil } from "lodash"

const GIFT_CARDS_BRAND_NAME = "Gift Cards"

// Get the height for an image when resizing it to a desired width, and
// maintaining aspect ratio.

export function getHeightForResizedWidth(
  originalWidth: number | null | undefined,
  originalHeight: number | null | undefined,
  desiredWidth: number,
) {
  // Guard against null sizes
  if (
    originalWidth === null ||
    originalHeight === null ||
    originalWidth === undefined ||
    originalHeight === undefined
  ) {
    return (desiredWidth * 2) / 3
  }

  return (desiredWidth / originalWidth) * originalHeight
}

export class GiftOptionUtils {
  static shouldDisplayTheFirstProductImgAsCoverImg(
    giftOption,
    flexPrice: number,
  ) {
    if (giftOption.name.startsWith("Gift Cards")) {
      return false
    }

    return giftOption.priceMaxWithShipping > flexPrice
  }
}

export class CollectionUtils {
  static maxBy(collection: any[], key: string) {
    if (!collection || collection.length === 0) {
      return null
    }

    return collection.reduce((value, acc) =>
      acc && acc[key] > value[key] ? acc : value,
    )
  }
}

export function joinWithCommaAnd(arr: string[]) {
  if (arr.length === 1) {
    return arr[0]
  } else {
    const arrCopy = [...arr]
    const firstEl = arrCopy.pop()
    return (
      arrCopy.join(", ") + (arrCopy.length > 1 ? ", and " : " and ") + firstEl
    )
  }
}

export function formatPrice(priceInCents: number, requireCents?: boolean) {
  // Is it a whole dollar amount?
  if (priceInCents % 100 === 0 && !requireCents) {
    return "$" + priceInCents / 100
  }

  return "$" + (priceInCents / 100).toFixed(2)
}

export function formatPriceInternational(params: {
  usdAmountInCents: number
  nativeCurrency?: string
  nativeAmountInCents?: number
}) {
  const { usdAmountInCents, nativeCurrency, nativeAmountInCents } = params

  if (nativeCurrency && nativeAmountInCents !== undefined) {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: nativeCurrency,
      maximumFractionDigits: 0,
    }).format(nativeAmountInCents / 100)
  } else {
    return formatPrice(usdAmountInCents)
  }
}

export const STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
]

export type GiftOptionPriceType = {
  id: string
  name: string
  priceMin?: number | null
  priceMax?: number | null
  isFlexGift: boolean
  brand: {
    id: string
    name: string
    shippingPrice?: number | null
    freeShippingMinimum?: number | null
  }
}

// Prefetch the given image only if the window is large.
export const prefetchLargeImage = (url: string) => {
  if (window.innerWidth >= 900) {
    const img = new Image()
    img.src = url
  }
}

export function isGiftCard(giftOption: GiftOptionPriceType) {
  return giftOption.brand.name === GIFT_CARDS_BRAND_NAME
}

export function productIsGiftCard(product: { brand: { name: string } }) {
  return product.brand.name === GIFT_CARDS_BRAND_NAME
}

export const isBook = (giftOption: GiftOptionPriceType) => {
  return giftOption.name === "Gift any book"
}

export const formattedGiftOptionPrice = (
  giftOption: GiftOptionPriceType,
  includeShipping = false,
) => {
  if (giftOption.priceMin === null || isBook(giftOption)) {
    return null
  }

  const priceMinAboveBrandFreeShippingThreshold =
    !isNil(giftOption.brand.freeShippingMinimum) &&
    giftOption.priceMin >= giftOption.brand.freeShippingMinimum

  const displayPriceMin =
    giftOption.priceMin +
    (includeShipping && !priceMinAboveBrandFreeShippingThreshold
      ? giftOption.brand.shippingPrice
      : 0)

  if (giftOption.isFlexGift) {
    return `${formatPrice(displayPriceMin)}+`
  }

  if (isGiftCard(giftOption)) {
    return "$5+"
  }

  return formatPriceMinRange(displayPriceMin, giftOption.priceMax)
}

export type ProductPriceType = {
  price: number
  brand: {
    id: string
    name: string
    shippingPrice?: number | null
    freeShippingMinimum?: number | null
  }
}

export const formattedProductPrice = (
  product: ProductPriceType,
  includeShipping = false,
) => {
  const priceMinAboveFreeShipping =
    !isNil(product.brand.freeShippingMinimum) &&
    product.price >= product.brand.freeShippingMinimum

  const displayPrice =
    product.price +
    (includeShipping && !priceMinAboveFreeShipping
      ? product.brand.shippingPrice
      : 0)

  return formatPrice(displayPrice)
}

// Format a price by showing the minimum in the range. If there are higher
// prices (i.e. priceMax != priceMin), then show a + to indicate higher amounts
// are present in the range
export function formatPriceMinRange(
  priceMin: number | null,
  priceMax: number | null,
) {
  let price = null
  if (priceMin !== null && priceMax !== null) {
    const productPricesAreSame = priceMin === priceMax

    price = formatPrice(priceMin)
    if (!productPricesAreSame) {
      price += "+"
    }
  }

  return price
}

interface ImgSpec {
  url?: string
  height?: number
  width?: number
}

export interface ImgSet {
  large?: ImgSpec
  xlarge2?: ImgSpec
}
